import { Html } from '@react-three/drei';
import { createPortal, useFrame } from '@react-three/fiber';
import {
  type CSSProperties,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Group, type Object3D, Vector3 } from 'three';

import Button from '@cartwhl/ui/input/Button';

import { usePlayerSettingsContext } from '../hooks/playerSettingsContext';
import { usePlayerActorScene } from '../hooks/usePlayerActorScene';
import { boundsForPlayerCharacter } from '../lib/fitPlayerCameraToCharacter';
import { usePlayerStore } from '../store/playerStore';

const HEAD_LIFT = 0.4;
const NAME_TAG_Z_INDEX_RANGE = [0, 0] as [number, number];
const NAME_TAG_HTML_STYLE = {
  pointerEvents: 'auto',
  willChange: 'transform',
} satisfies CSSProperties;

/** Stable accent colors for multi-reference scenes (index 0 → first entry). */
export const ACTOR_ACCENT_BG_CLASSES = [
  'bg-blue',
  'bg-red',
  'bg-purple',
  'bg-orange',
] as const;

function referenceNameSortOrder(referenceName: string): number {
  if (referenceName === 'character') {
    return 0;
  }

  const match = /^character(\d+)$/.exec(referenceName);
  return match ? parseInt(match[1]!, 10) - 1 : Number.MAX_SAFE_INTEGER;
}

function sortReferenceActorIds(actorIds: readonly string[]): string[] {
  return [...actorIds].sort(
    (left, right) =>
      referenceNameSortOrder(left) - referenceNameSortOrder(right),
  );
}

function resolveReferenceActorDisplayLabel(
  actorId: string,
  sortedActorIds: readonly string[],
): string {
  const index = sortedActorIds.indexOf(actorId);
  return index >= 0 ? `Actor ${index + 1}` : actorId;
}

function resolveReferenceActorAccentIndex(
  actorId: string,
  sortedActorIds: readonly string[],
): number {
  const index = sortedActorIds.indexOf(actorId);
  return index >= 0 ? index : 0;
}

export interface ActorHeadActionOverlay {
  label: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
}

interface ReferenceNameTagBubbleProps {
  actorId: string;
  active: boolean;
  headAction?: ActorHeadActionOverlay;
  accentIndex: number;
  label: string;
  dimInactive: boolean;
  onActorClick?: (actorId: string) => void;
  sceneOverride?: Object3D | null;
}

function ReferenceNameTagBubble({
  active,
  actorId,
  headAction,
  accentIndex,
  label,
  dimInactive,
  onActorClick,
  sceneOverride,
}: ReferenceNameTagBubbleProps) {
  const store = usePlayerStore();
  const headBoneName = store.state.actorsById[actorId]?.headBoneName;
  const registeredRoot = usePlayerActorScene(actorId);
  const root = sceneOverride === undefined ? registeredRoot : sceneOverride;
  const head = useMemo(() => {
    if (!root) {
      return null;
    }
    const trimmed = headBoneName?.trim();
    if (trimmed) {
      const named = root.getObjectByName(trimmed);
      if (named) {
        return named;
      }
    }
    return (
      root.getObjectByName('c_head') ?? root.getObjectByName('head') ?? null
    );
  }, [headBoneName, root]);
  const anchor = head ?? root;
  const groupRef = useRef<Group>(null);
  const worldPosition = useMemo(() => new Vector3(), []);
  const localPosition = useMemo(() => new Vector3(), []);

  // Precise skinned bounds are expensive when name tags mount; a fixed head offset is enough here.
  const headWorldYOffset = head ? HEAD_LIFT : null;

  const fallbackPosition = useMemo(() => {
    if (!root || !anchor || head) {
      return null;
    }

    const hull = boundsForPlayerCharacter(root);
    if (hull.isEmpty() || !Number.isFinite(hull.max.y)) {
      return null;
    }

    const worldTop = new Vector3(
      (hull.min.x + hull.max.x) * 0.5,
      hull.max.y + HEAD_LIFT,
      (hull.min.z + hull.max.z) * 0.5,
    );

    return anchor.worldToLocal(worldTop);
  }, [anchor, head, root]);

  const accent =
    ACTOR_ACCENT_BG_CLASSES[accentIndex % ACTOR_ACCENT_BG_CLASSES.length]!;
  const backgroundClassName = !dimInactive || active ? accent : 'bg-gray-60';
  const handleClick = useCallback(() => {
    store.setActiveActor(actorId);
    onActorClick?.(actorId);
  }, [actorId, onActorClick, store]);

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;

    if (head && headWorldYOffset !== null) {
      head.getWorldPosition(worldPosition);
      worldPosition.y += headWorldYOffset;
      localPosition.copy(worldPosition);
      head.worldToLocal(localPosition);
      group.position.copy(localPosition);
      group.visible = true;
      return;
    }

    if (fallbackPosition) {
      group.position.copy(fallbackPosition);
      group.visible = true;
      return;
    }

    group.visible = false;
  });

  if (!anchor) {
    return null;
  }

  return createPortal(
    <group ref={groupRef} visible={false}>
      <Html
        center
        distanceFactor={11}
        style={NAME_TAG_HTML_STYLE}
        zIndexRange={NAME_TAG_Z_INDEX_RANGE}
      >
        {headAction ? (
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="pointer-events-auto shadow-sm"
            disabled={headAction.disabled}
            loading={headAction.loading}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              headAction.onClick();
            }}
          >
            {headAction.label}
          </Button>
        ) : (
          <button
            type="button"
            className={`focus-visible:outline-hidden inline-flex cursor-pointer select-none whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-semibold leading-none tracking-tight text-white shadow-sm transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-white ${backgroundClassName}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              handleClick();
            }}
          >
            {label}
          </button>
        )}
      </Html>
    </group>,
    anchor,
  );
}

/**
 * Numbered labels injected into each reference object's scene.
 * Tags use each actor's configured head bone name in player state when set
 * (from character mapping), otherwise the object named `head`, otherwise the scene root.
 * Tags use fixed Html z-indexing so orbiting the camera does not force DOM
 * overlay restacking on pointer release.
 */
export interface ReferenceNameTagsProps {
  actorHeadActionByActorId?: ReadonlyMap<string, ActorHeadActionOverlay>;
  /** Explicit display labels keyed by reference actor id. */
  actorDisplayLabelsByActorId?: ReadonlyMap<string, string>;
  actorNameTagSceneOverrides?: ReadonlyMap<string, Object3D | null>;
  onActorClick?: (actorId: string) => void;
}

export default function ReferenceNameTags({
  actorHeadActionByActorId,
  actorDisplayLabelsByActorId,
  actorNameTagSceneOverrides,
  onActorClick,
}: ReferenceNameTagsProps) {
  const store = usePlayerStore();
  const {
    settings: { scene },
  } = usePlayerSettingsContext();
  const actorIds = Object.keys(store.state.actorsById);
  const sortedActorIds = useMemo(
    () => sortReferenceActorIds(actorIds),
    [actorIds],
  );
  const activeActorId = store.state.activeActorId;
  const dimInactive = scene.dimInactiveActorNameTags;

  const visibleActorIds = actorIds.filter(
    (actorId) =>
      actorHeadActionByActorId?.has(actorId) ||
      (scene.showActorNameTags && actorIds.length >= 2),
  );

  if (visibleActorIds.length === 0) {
    return null;
  }

  return (
    <>
      {visibleActorIds.map((actorId) => (
        <ReferenceNameTagBubble
          key={actorId}
          actorId={actorId}
          active={actorId === activeActorId}
          headAction={actorHeadActionByActorId?.get(actorId)}
          accentIndex={resolveReferenceActorAccentIndex(
            actorId,
            sortedActorIds,
          )}
          label={
            actorDisplayLabelsByActorId?.get(actorId) ??
            resolveReferenceActorDisplayLabel(actorId, sortedActorIds)
          }
          dimInactive={dimInactive}
          onActorClick={onActorClick}
          sceneOverride={actorNameTagSceneOverrides?.get(actorId)}
        />
      ))}
    </>
  );
}
